import { CircularProgress, TableRow } from '@mui/material';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  IssuePayment,
  StudyIssuanceRequest,
} from '../../../../shared/api/hscan/hospital';
import { useIssuancesInPayment } from '../../../../shared/hooks/hospitalHook';
import { HscanTableCell } from '../../common/components/SortableTable';
import { handleCellStyle } from '../../common/components/SortableTable/RenderTable';
import { useModalityLabel } from '../../hooks/modalityHook';

import { StatusMessage } from './StatusMessage';

const StudyIssuanceRequestsTableRow = styled(TableRow)`
  background: #f8f9fd;
`;

export interface StudyIssuanceRequestsTableProps {
  payment: IssuePayment;
  onClickStatus?(request: StudyIssuanceRequest): void;
}
export const StudyIssuanceRequestsTable = ({
  payment,
  onClickStatus,
}: StudyIssuanceRequestsTableProps) => {
  const { data: issuanceRequests, isFetching } = useIssuancesInPayment({
    paymentId: payment.id,
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table.requestTable',
  });
  const modalityLabel = useModalityLabel();

  const columnHelper = useMemo(
    () => createColumnHelper<StudyIssuanceRequest>(),
    [],
  );
  const cellOptions = useMemo(
    () => [
      columnHelper.display({
        id: '__PLACEHOLDER__',
        meta: {
          cellStyle: { background: 'white', borderBottom: { style: 'none' } },
          headerStyle: { background: 'white', borderBottom: { style: 'none' } },
        },
      }),
      columnHelper.accessor('studyDate', {
        header: t('studyDate') as string,
        cell: ({ getValue }) => moment(getValue()).format('YYYY.MM.DD'),
      }),
      columnHelper.accessor('hospitalName', {
        header: t('hospitalName') as string,
      }),
      columnHelper.accessor('modalities', {
        header: t('modalities') as string,
        cell: ({ getValue }) =>
          getValue()
            .map(modality => modalityLabel(modality))
            .join(', '),
      }),
      columnHelper.accessor('studyDescription', {
        header: t('studyDescription') as string,
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: t('status') as string,
        cell: ({ getValue, row: { original: issuanceRequest } }) => (
          <StatusMessage
            onClick={() => onClickStatus?.(issuanceRequest)}
            status={getValue()}
          />
        ),
      }),
      columnHelper.display({
        id: '__EMPTY_CELL_1__',
      }),
    ],
    [columnHelper, t, modalityLabel, onClickStatus],
  );

  const table = useReactTable({
    data: issuanceRequests ?? [],
    columns: cellOptions,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {issuanceRequests ? (
        <>
          <StudyIssuanceRequestsTableRow>
            {table.getLeafHeaders().map(header => (
              <HscanTableCell
                key={header?.id}
                {...handleCellStyle(header.column.columnDef.meta?.headerStyle)}
                align="center">
                {
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  ) as React.ReactNode
                }
              </HscanTableCell>
            ))}
          </StudyIssuanceRequestsTableRow>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map(cell => (
                <HscanTableCell
                  key={cell.id}
                  {...handleCellStyle(cell.column.columnDef.meta?.cellStyle)}
                  align="center">
                  {
                    flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    ) as React.ReactNode
                  }
                </HscanTableCell>
              ))}
            </TableRow>
          ))}
        </>
      ) : (
        isFetching && (
          <TableRow>
            <HscanTableCell />
            <HscanTableCell colSpan={cellOptions.length} align="center">
              <CircularProgress />
            </HscanTableCell>
          </TableRow>
        )
      )}
    </>
  );
};

export const RequestsTableForSimplePayment = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table.requestTable',
  });

  return (
    <StudyIssuanceRequestsTableRow>
      <HscanTableCell
        {...handleCellStyle({
          background: 'white',
          borderBottom: { style: 'none' },
        })}
      />
      <HscanTableCell align="center">{t('simplePayment')}</HscanTableCell>
      <HscanTableCell />
      <HscanTableCell />
      <HscanTableCell />
      <HscanTableCell />
      <HscanTableCell />
      <HscanTableCell />
    </StudyIssuanceRequestsTableRow>
  );
};
